import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import {
    QUERY_PICKUP_DRIVER_TRACKING_KPI,
    QUERY_PICKUP_DRIVER_TRACKING_POSITIONS,
    QUERY_PICKUP_DRIVER_TRACKING_POSITIONS_FULL_DAY,
    QUERY_PICKUP_DRIVER_TRACKING_SNAPSHOT,
} from '../../../graphql/queries/pickupDriverTracking/queryPickupDriverTracking';
import type {
    PickupTrackingDriverPositionType,
    QueryPickupDriverTrackingKpiResponse,
    QueryPickupDriverTrackingPositionsResponse,
    QueryPickupDriverTrackingSnapshotResponse,
} from '../../../graphql/types/pickupDriverTracking/PickupDriverTrackingTypes';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { hubLabel as toHubLabel } from '../../central/delivery-monitor/deliveryMonitorMotion';
import { diffPickupEvents, latestPickupEvents } from './pickupTrackingLogic';
import { mergeLastKnown, toKpiHistory, toTrackingDriver } from './pickupTrackingMapping';
import type {
    PickupTrackingData,
    TrackingDriver,
    TrackingFeedEvent,
    TrackingHub,
    TrackingKpiData,
    TrackingSnapshot,
} from './pickupTrackingTypes';

/** 位置轮询周期，同派送大屏；小车滑行总时长对齐它 */
export const PICKUP_POLL_POSITIONS_MS = 20_000;
/** 快照（司机 / 站点）轮询周期：站点状态与小车联动，同位置 20 秒（同派送 POLL_DRIVERS_MS） */
const POLL_SNAPSHOT_MS = PICKUP_POLL_POSITIONS_MS;
/** KPI 轮询周期：数仓 10 分钟一拍，同派送 POLL_KPI_MS */
const POLL_KPI_MS = 600_000;
/** 数仓 KPI slot 周期，补间总时长对齐它 */
const KPI_SLOT_MS = 600_000;
/** feed 一批事件摊匀弹出的时长：2.1 约 10 分钟刷新一次，在下一批到来前弹完；指定时刻按分钟步进，1 分钟内弹完 */
const FEED_SPAN_MS = 600_000;
const FEED_SPAN_AS_OF_MS = 60_000;

// 同一范围内换变量（指定查询时刻每分钟步进）时沿用上一份数据：Apollo 换变量加载期间 data 为 undefined，
// 不兜底会每分钟闪一次空屏。范围变了不沿用，免得旧范围的 KPI 被补间写进新范围的基线（同派送大屏）
const useScopedPrevious = <T,>(data: T | undefined, scopeKey: string): T | undefined => {
    const lastRef = useRef<{ scopeKey: string; data: T } | null>(null);
    if (data !== undefined) lastRef.current = { scopeKey, data };
    if (data !== undefined) return data;
    return lastRef.current?.scopeKey === scopeKey ? lastRef.current.data : undefined;
};

export interface PickupTrackingDataParams {
    enabled: boolean;
    /** 大屏内选中的 hub；null 取顶栏当前 hub */
    hubId: string | null;
    /** 美西揽收业务日 */
    taskDate: string;
    /** 位置查询时刻（按位置轮询周期步进）；null = 实时 */
    asOfMs: number | null;
    /** 快照查询时刻（取整到分钟）；null = 实时 */
    asOfMinuteMs: number | null;
    /** 指定查询时刻的设定基准；变了视为换范围 */
    asOfBaseMs: number | null;
    /**
     * 实时 feed：前后两份快照对比出新完成的站点，按 spanMs 摊匀弹出；
     * 范围内第一份快照（冷启动）给最近几条兜底，spanMs = 0 直接弹
     */
    onPickupEvents?: (events: TrackingFeedEvent[], spanMs: number) => void;
}

let feedSeq = 0;

export const usePickupTrackingData = ({
    enabled,
    hubId,
    taskDate,
    asOfMs,
    asOfMinuteMs,
    asOfBaseMs,
    onPickupEvents,
}: PickupTrackingDataParams): PickupTrackingData => {
    const { currentWarehouse } = useWarehouseContext();
    const effectiveHubId = hubId ?? (currentWarehouse?.id ? String(currentWarehouse.id) : null);
    const canQuery = enabled && effectiveHubId !== null;
    const scopeKey = `${effectiveHubId ?? ''}|${taskDate}|${asOfBaseMs ?? ''}`;

    const { data: snapshotData, loading: snapshotLoading } = useQuery<QueryPickupDriverTrackingSnapshotResponse>(
        QUERY_PICKUP_DRIVER_TRACKING_SNAPSHOT,
        {
            variables: { hubId: Number(effectiveHubId), taskDate, asOfMs: asOfMinuteMs },
            skip: !canQuery,
            fetchPolicy: 'cache-and-network',
            // 指定时刻由 asOfMinuteMs 步进驱动刷新，不再轮询
            pollInterval: asOfMinuteMs === null ? POLL_SNAPSHOT_MS : 0,
        },
    );
    const raw = useScopedPrevious(snapshotData?.pickupDriverTrackingSnapshot, scopeKey);

    const { data: kpiData } = useQuery<QueryPickupDriverTrackingKpiResponse>(QUERY_PICKUP_DRIVER_TRACKING_KPI, {
        variables: { hubId: Number(effectiveHubId), taskDate, asOfMs: asOfMinuteMs },
        skip: !canQuery,
        fetchPolicy: 'cache-and-network',
        // 指定时刻由 asOfMinuteMs 步进驱动刷新，不再轮询
        pollInterval: asOfMinuteMs === null ? POLL_KPI_MS : 0,
    });
    const kpiRaw = useScopedPrevious(kpiData?.pickupDriverTrackingKpi, scopeKey);

    const { data: positionsData, loading: positionsLoading } = useQuery<QueryPickupDriverTrackingPositionsResponse>(
        QUERY_PICKUP_DRIVER_TRACKING_POSITIONS,
        {
            variables: { hubId: Number(effectiveHubId), taskDate, asOfMs },
            skip: !canQuery,
            fetchPolicy: 'network-only',
            // 指定时刻由 asOfMs 步进驱动刷新，不再轮询，避免同一轮打两次
            pollInterval: asOfMs === null ? PICKUP_POLL_POSITIONS_MS : 0,
        },
    );
    // 位置也只在同一范围内沿用：跨范围沿用旧位置会让新范围的小车先落在旧位置，
    // 新范围轨迹更早（指定时刻）时全被滑行过滤掉，小车就一直停在旧位置
    const positions = useScopedPrevious(positionsData?.pickupDriverTrackingPositions, scopeKey) ?? null;

    // 首屏查一整天：每个范围查一次当天 0 点起每个司机的最后已知位置，不轮询；指定时刻按设定基准查，之后靠轮询前进
    const { data: fullDayData, loading: fullDayLoading } = useQuery<QueryPickupDriverTrackingPositionsResponse>(
        QUERY_PICKUP_DRIVER_TRACKING_POSITIONS_FULL_DAY,
        {
            variables: { hubId: Number(effectiveHubId), taskDate, asOfMs: asOfBaseMs },
            skip: !canQuery,
            fetchPolicy: 'network-only',
        },
    );
    const fullDay = useScopedPrevious(fullDayData?.pickupDriverTrackingPositions, scopeKey) ?? null;

    // 当天最后已知位置在同一范围内累积：本轮没上报的司机留在最后位置（地图上变灰），范围变了从头算
    const lastKnownRef = useRef<{ scopeKey: string; byDriver: Map<string, PickupTrackingDriverPositionType> } | null>(
        null,
    );
    const lastKnown = useMemo(() => {
        const prev = lastKnownRef.current?.scopeKey === scopeKey ? lastKnownRef.current.byDriver : new Map();
        const byDriver = mergeLastKnown(prev, fullDay, positions);
        lastKnownRef.current = { scopeKey, byDriver };
        return byDriver;
    }, [scopeKey, fullDay, positions]);

    const drivers = useMemo<TrackingDriver[]>(() => {
        if (!raw) return [];
        const byDriver = new Map<string, PickupTrackingDriverPositionType>();
        (positions ?? []).forEach((p) => byDriver.set(String(p.driverAccountId), p));
        return (raw.drivers ?? []).map((d) => {
            const key = String(d.driverAccountId);
            return toTrackingDriver(d, byDriver.get(key), lastKnown.get(key));
        });
    }, [raw, positions, lastKnown]);

    // feed：同一范围内快照前进时对比站点状态；位置轮询不改站点，只看快照。
    // cache-and-network 先给的缓存快照不当基准（切回看过的 hub 时它可能很旧，一比会冒出一大批），等网络结果
    const eventRef = useRef(onPickupEvents);
    eventRef.current = onPickupEvents;
    const lastRef = useRef<{ scopeKey: string; raw: unknown; drivers: TrackingDriver[] } | null>(null);
    useEffect(() => {
        if (!raw || snapshotLoading) return;
        const last = lastRef.current;
        if (last?.raw === raw) return;
        lastRef.current = { scopeKey, raw, drivers };
        const cold = !last || last.scopeKey !== scopeKey;
        const events = cold ? latestPickupEvents(drivers) : diffPickupEvents(last.drivers, drivers);
        const spanMs = cold ? 0 : asOfBaseMs === null ? FEED_SPAN_MS : FEED_SPAN_AS_OF_MS;
        eventRef.current?.(
            events.map((e) => ({ ...e, id: ++feedSeq })),
            spanMs,
        );
    }, [raw, drivers, scopeKey, snapshotLoading, asOfBaseMs]);

    // 只有左侧选中的这一个 hub：名字取顶栏 hub，坐标取快照。只依赖原始值，轮询不重建，地图 hub effect 不重跑
    // 同派送大屏 HUB：名称去掉末尾「HUB」（LAX HUB → LAX），不用 sortingCode（LA 的 sortingCode 是 LA）
    const hubLabel = (currentWarehouse?.name && toHubLabel({ name: currentWarehouse.name })) || effectiveHubId;
    const rawHubLat = raw?.hubLatitude;
    const rawHubLng = raw?.hubLongitude;
    const hub = useMemo<TrackingHub | null>(() => {
        if (effectiveHubId === null || !hubLabel) return null;
        const lat = rawHubLat === null || rawHubLat === undefined ? null : Number(rawHubLat);
        const lng = rawHubLng === null || rawHubLng === undefined ? null : Number(rawHubLng);
        const located = lat !== null && lng !== null;
        return {
            id: effectiveHubId,
            code: hubLabel,
            names: { zh: hubLabel, en: hubLabel },
            lat: located ? lat : null,
            lng: located ? lng : null,
        };
    }, [effectiveHubId, hubLabel, rawHubLat, rawHubLng]);

    const snapshot = useMemo<TrackingSnapshot | null>(() => {
        if (!enabled || !raw || effectiveHubId === null || !hub) return null;
        return {
            hub,
            hubId: effectiveHubId,
            drivers,
            longStayThresholdMin: Number(raw.longStayThresholdMin),
            positionPollMs: PICKUP_POLL_POSITIONS_MS,
        };
    }, [enabled, raw, effectiveHubId, hub, drivers]);

    // KPI 不等快照：只要 KPI 回来就出（同派送大屏 KPI 与司机列表互不依赖）
    const kpi = useMemo<TrackingKpiData | null>(() => {
        if (!enabled || !kpiRaw || effectiveHubId === null) return null;
        return { hubId: effectiveHubId, history: toKpiHistory(kpiRaw), slotMs: KPI_SLOT_MS };
    }, [enabled, kpiRaw, effectiveHubId]);

    const positionCount = lastKnown.size;
    const anyPositionsLoading = positionsLoading || fullDayLoading;
    return useMemo(
        () => ({ snapshot, kpi, positionsLoading: anyPositionsLoading, positionCount }),
        [snapshot, kpi, anyPositionsLoading, positionCount],
    );
};
